import type { FormEvent, ReactElement, ReactNode } from 'react';
import { cloneElement, isValidElement } from 'react';
import { cn } from '../ui/utils';
import { useScopedInertiaLoading } from '../../hooks/useInertiaLoading';
import { SearchField } from '../forms/SearchField';
import { FilterBarLoadingProvider } from './filterBarContext';

type FilterBarProps = {
    onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
    search?: ReactNode;
    filters?: ReactNode;
    actions?: ReactNode;
    footer?: ReactNode;
    className?: string;
    children?: ReactNode;
};

/**
 * Shell de filtros (search + selects + actions).
 * A página controla estado e `router.get` — use `toInertiaUrl()` nas visitas
 * para evitar Mixed Content quando o backend gera URLs absolutas em http.
 *
 * Envolve o formulário com loading scoped: use `FilterSubmitButton` nos actions.
 */
function FilterBar({
    onSubmit,
    search,
    filters,
    actions,
    footer,
    className,
    children,
}: FilterBarProps) {
    const { loading: filterLoading, arm } = useScopedInertiaLoading();

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        arm();
        onSubmit?.(event);
    };

    const renderSearch = (node: ReactNode) => {
        if (isValidElement(node) && node.type === SearchField) {
            return cloneElement(node as ReactElement<{ filter?: boolean }>, { filter: true });
        }

        return node;
    };

    const formContent = children ?? (
        <>
            {search ? <div className="nx-filters-search">{renderSearch(search)}</div> : null}
            {filters ? <div className="nx-filters-selects">{filters}</div> : null}
            {actions ? (
                <div className="nx-filters-actions">
                    <div className="nx-filter-field nx-filter-field--action">
                        <span className="nx-filter-field__label-spacer" aria-hidden="true">
                            {'\u00a0'}
                        </span>
                        <div className="nx-filter-actions-row">
                            {actions}
                        </div>
                    </div>
                </div>
            ) : null}
            {footer ? <div className="nx-filters-footer">{footer}</div> : null}
        </>
    );

    return (
        <FilterBarLoadingProvider loading={filterLoading}>
            <form className={cn('nx-filters', className)} onSubmit={handleSubmit}>
                {formContent}
            </form>
        </FilterBarLoadingProvider>
    );
}

type FilterFieldProps = {
    label: string;
    children: ReactNode;
    className?: string;
};

function FilterField({ label, children, className }: FilterFieldProps) {
    return (
        <label className={cn('nx-filter-field', className)}>
            <span>{label}</span>
            {children}
        </label>
    );
}

export { FilterBar, FilterField };
export { FilterSubmitButton } from './FilterSubmitButton';
export type { FilterBarProps, FilterFieldProps };
